import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Marketing = () => {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'email',
    status: 'draft',
    target_clients: '',
    open_rate: 0,
    facebook_url: '',
    facebook_username: '',
    facebook_password: '',
    instagram_url: '',
    instagram_username: '',
    instagram_password: '',
    linkedin_url: '',
    linkedin_username: '',
    linkedin_password: '',
    twitter_url: '',
    twitter_username: '',
    twitter_password: '',
    custom_platforms: []
  });
  const [editingId, setEditingId] = useState(null);
  const [newPlatform, setNewPlatform] = useState({ name: '', url: '', username: '', password: '' });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/campaigns`);
      setCampaigns(res.data);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/campaigns/${editingId}`, formData);
        toast.success('Кампанията бе актуализирана!');
      } else {
        await axios.post(`${API}/campaigns`, formData);
        toast.success('Кампанията бе добавена!');
      }
      fetchCampaigns();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Сигурни ли сте?')) {
      try {
        await axios.delete(`${API}/campaigns/${id}`);
        toast.success('Кампанията бе изтрита!');
        fetchCampaigns();
      } catch (error) {
        toast.error('Грешка при изтриване');
      }
    }
  };

  const handleEdit = (campaign) => {
    setFormData({
      ...campaign,
      custom_platforms: campaign.custom_platforms || []
    });
    setEditingId(campaign.id);
    setDialogOpen(true);
  };

  const addCustomPlatform = () => {
    if (!newPlatform.name || !newPlatform.url) {
      toast.error('Моля, въведете име и URL на платформата');
      return;
    }
    setFormData({
      ...formData,
      custom_platforms: [...formData.custom_platforms, { ...newPlatform }]
    });
    setNewPlatform({ name: '', url: '', username: '', password: '' });
    toast.success('Платформата е добавена');
  };

  const removeCustomPlatform = (index) => {
    const updated = formData.custom_platforms.filter((_, i) => i !== index);
    setFormData({ ...formData, custom_platforms: updated });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'email',
      status: 'draft',
      target_clients: '',
      open_rate: 0,
      facebook_url: '',
      facebook_username: '',
      facebook_password: '',
      instagram_url: '',
      instagram_username: '',
      instagram_password: '',
      linkedin_url: '',
      linkedin_username: '',
      linkedin_password: '',
      twitter_url: '',
      twitter_username: '',
      twitter_password: '',
      custom_platforms: []
    });
    setNewPlatform({ name: '', url: '', username: '', password: '' });
    setEditingId(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'badge-gray',
      active: 'badge-blue',
      completed: 'badge-green'
    };
    return colors[status] || 'badge-gray';
  };

  const statusLabels = {
    draft: 'Чернова',
    active: 'Активна',
    completed: 'Завършена'
  };

  const typeLabels = {
    email: 'Email',
    promotion: 'Промоция'
  };

  return (
    <div className="page-container" data-testid="marketing-page">
      <div className="page-header">
        <h1 className="page-title">{t('marketing')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-campaign-btn">
              <Plus size={20} /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? t('edit') : t('add')} Кампания</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="campaign-form" data-testid="campaign-form">
              <Input
                placeholder="Име на кампания (напр. Промоция Черен петък 2025)"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
              <Select value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="promotion">Промоция</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Чернова</SelectItem>
                  <SelectItem value="active">Активна</SelectItem>
                  <SelectItem value="completed">Завършена</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Целеви клиенти (описание на аудиторията)"
                value={formData.target_clients}
                onChange={(e) => setFormData({...formData, target_clients: e.target.value})}
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Open Rate (процент отворени имейли, напр. 45.5)"
                value={formData.open_rate}
                onChange={(e) => setFormData({...formData, open_rate: parseFloat(e.target.value)})}
              />
              
              <div className="social-media-section">
                <h4>Social Media достъпи</h4>
                
                <div className="social-platform">
                  <label className="platform-label">Facebook</label>
                  <Input
                    placeholder="Facebook линк (напр. facebook.com/tracking-b)"
                    value={formData.facebook_url}
                    onChange={(e) => setFormData({...formData, facebook_url: e.target.value})}
                  />
                  <Input
                    placeholder="Username"
                    value={formData.facebook_username}
                    onChange={(e) => setFormData({...formData, facebook_username: e.target.value})}
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={formData.facebook_password}
                    onChange={(e) => setFormData({...formData, facebook_password: e.target.value})}
                  />
                </div>

                <div className="social-platform">
                  <label className="platform-label">Instagram</label>
                  <Input
                    placeholder="Instagram линк (напр. instagram.com/tracking_b)"
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({...formData, instagram_url: e.target.value})}
                  />
                  <Input
                    placeholder="Username"
                    value={formData.instagram_username}
                    onChange={(e) => setFormData({...formData, instagram_username: e.target.value})}
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={formData.instagram_password}
                    onChange={(e) => setFormData({...formData, instagram_password: e.target.value})}
                  />
                </div>

                <div className="social-platform">
                  <label className="platform-label">LinkedIn</label>
                  <Input
                    placeholder="LinkedIn линк (напр. linkedin.com/company/tracking-b)"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                  />
                  <Input
                    placeholder="Username"
                    value={formData.linkedin_username}
                    onChange={(e) => setFormData({...formData, linkedin_username: e.target.value})}
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={formData.linkedin_password}
                    onChange={(e) => setFormData({...formData, linkedin_password: e.target.value})}
                  />
                </div>

                <div className="social-platform">
                  <label className="platform-label">Twitter/X</label>
                  <Input
                    placeholder="Twitter линк (напр. twitter.com/tracking_b)"
                    value={formData.twitter_url}
                    onChange={(e) => setFormData({...formData, twitter_url: e.target.value})}
                  />
                  <Input
                    placeholder="Username"
                    value={formData.twitter_username}
                    onChange={(e) => setFormData({...formData, twitter_username: e.target.value})}
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={formData.twitter_password}
                    onChange={(e) => setFormData({...formData, twitter_password: e.target.value})}
                  />
                </div>

                {/* Custom Platforms Section */}
                <div className="custom-platforms-section">
                  <h4>Допълнителни платформи</h4>
                  
                  {/* Add New Platform Form */}
                  <div className="add-platform-form">
                    <Input
                      placeholder="Име на платформата (напр. TikTok, YouTube)"
                      value={newPlatform.name}
                      onChange={(e) => setNewPlatform({...newPlatform, name: e.target.value})}
                    />
                    <Input
                      placeholder="URL/Link"
                      value={newPlatform.url}
                      onChange={(e) => setNewPlatform({...newPlatform, url: e.target.value})}
                    />
                    <Input
                      placeholder="Username (опционално)"
                      value={newPlatform.username}
                      onChange={(e) => setNewPlatform({...newPlatform, username: e.target.value})}
                    />
                    <Input
                      type="password"
                      placeholder="Password (опционално)"
                      value={newPlatform.password}
                      onChange={(e) => setNewPlatform({...newPlatform, password: e.target.value})}
                    />
                    <Button type="button" onClick={addCustomPlatform} variant="secondary">
                      <Plus size={16} /> Добави платформа
                    </Button>
                  </div>

                  {/* List of Added Custom Platforms */}
                  {formData.custom_platforms.length > 0 && (
                    <div className="custom-platforms-list">
                      <h5>Добавени платформи:</h5>
                      {formData.custom_platforms.map((platform, index) => (
                        <div key={index} className="custom-platform-item">
                          <div>
                            <strong>{platform.name}</strong>
                            <p>{platform.url}</p>
                            {platform.username && <span>Username: {platform.username}</span>}
                          </div>
                          <Button 
                            type="button" 
                            variant="destructive" 
                            size="sm"
                            onClick={() => removeCustomPlatform(index)}
                          >
                            <Trash2 size={14} /> Премахни
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" data-testid="save-campaign-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="campaigns-grid">
        {campaigns.map(campaign => (
          <div key={campaign.id} className="campaign-card" data-testid={`campaign-card-${campaign.id}`}>
            <div className="campaign-card-header">
              <div>
                <h3>{campaign.name}</h3>
                <p className="campaign-type">{typeLabels[campaign.type]}</p>
              </div>
              <span className={`status-badge ${getStatusColor(campaign.status)}`}>
                {statusLabels[campaign.status]}
              </span>
            </div>

            <div className="campaign-card-body">
              {campaign.target_clients && (
                <div className="campaign-info-row">
                  <span>Целеви клиенти:</span>
                  <span>{campaign.target_clients}</span>
                </div>
              )}
              <div className="campaign-info-row">
                <span>Open Rate:</span>
                <span className="open-rate">{campaign.open_rate}%</span>
              </div>
            </div>

            <div className="campaign-card-footer">
              <Button variant="ghost" size="sm" onClick={() => handleEdit(campaign)} data-testid={`edit-campaign-${campaign.id}`}>
                <Edit size={16} /> {t('edit')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(campaign.id)} data-testid={`delete-campaign-${campaign.id}`}>
                <Trash2 size={16} /> {t('delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Marketing;
